/**
 * How much of a facility belongs to this lender (LEN-1877).
 *
 * One rule for every screen that answers the question -- the facility's
 * General information today, the Insights MAXIMUM CREDIT AMOUNT card and the
 * portfolio Overview next -- so the same facility never shows two different
 * percentages in two tabs.
 *
 * What a card says in each case is pinned in `docs/facility-card-states.md`.
 * Everything here returns `undefined` rather than `0` when the figure is not
 * known: a zero claims a measurement nobody made, and the caller decides how
 * to name the absence.
 */
import {
  LENDER_POSITIONS,
  type LenderParticipation,
} from '@models/operation/lender-participation.model';

/** A participation whose commitment can be read as a figure. */
type WithCommitment = LenderParticipation & { committed_amount: number };

/** A sole lender: the whole facility is theirs. */
export function isBilateral(participation: LenderParticipation): boolean {
  return participation.position === 'SOLE_LENDER';
}

/**
 * Whether the position is one the catalog knows. An unknown or missing one is
 * never treated as a syndicate: that would claim other lenders exist with
 * nothing to back it, and a mis-captured sole lender would read
 * "of $X (100%)".
 */
export function isKnownPosition(participation: LenderParticipation): boolean {
  return (LENDER_POSITIONS as readonly (string | null | undefined)[]).includes(
    participation.position
  );
}

/** Whether there is a usable commitment figure. */
export function hasCommitment(
  participation: LenderParticipation | null | undefined
): participation is WithCommitment {
  const amount = participation?.committed_amount;

  return amount != null && Number.isFinite(amount) && amount > 0;
}

/** A ceiling can be compared against only if it is a number above zero. */
export function isUsableCeiling(
  totalCreditAmount: number | null | undefined
): totalCreditAmount is number {
  return (
    totalCreditAmount != null &&
    Number.isFinite(totalCreditAmount) &&
    totalCreditAmount > 0
  );
}

/**
 * The amount that heads the card: the lender's commitment when known, the
 * contract's ceiling when not, and `undefined` when there is neither -- never
 * `$0`, which would claim a commitment measured at zero.
 */
export function lenderAmount(
  participation: LenderParticipation | null | undefined,
  totalCreditAmount: number | null | undefined
): number | undefined {
  if (hasCommitment(participation)) {
    return participation.committed_amount;
  }

  return isUsableCeiling(totalCreditAmount) ? totalCreditAmount : undefined;
}

/**
 * The share of the ceiling the commitment represents, in percent. Derived from
 * the two amounts, not read from `participation_pct`, so it always matches the
 * figures next to it. `undefined` when there is nothing to divide.
 */
export function lenderSharePct(
  participation: LenderParticipation | null | undefined,
  totalCreditAmount: number | null | undefined
): number | undefined {
  if (!hasCommitment(participation) || !isUsableCeiling(totalCreditAmount)) {
    return undefined;
  }

  return (participation.committed_amount / totalCreditAmount) * 100;
}

/**
 * The amber notice of case 05, the same on every card that shows it
 * (LEN-2365).
 */
export const TOTAL_NOT_REPORTED = 'Total credit amount not reported';

export const EXCEEDS_CEILING_NOTE =
  'Commitment exceeds the maximum credit amount';

/**
 * The commitment exceeds the reported ceiling. A contradiction, not an
 * absence: both figures cannot be true at once, so it is named apart (case 05
 * of the doc) and never drawn as a share above 100%.
 */
export function exceedsCeiling(
  participation: LenderParticipation | null | undefined,
  totalCreditAmount: number | null | undefined
): boolean {
  if (!hasCommitment(participation) || !isUsableCeiling(totalCreditAmount)) {
    return false;
  }

  return participation.committed_amount > totalCreditAmount;
}

/** A facility as the portfolio payload carries it, for the share below. */
type PortfolioFacility = LenderParticipation & {
  total_credit_amount?: number | null;
};

/**
 * The portfolio's share and how much of the portfolio it could measure.
 *
 * `counted` and `excluded` are not decoration: a percentage over 1 of 5
 * facilities says something very different from the same percentage over 5 of
 * 5, and the caller has to be able to tell them apart (MR !1366 S3).
 */
export interface PortfolioShare {
  /** `undefined` when no facility had both amounts. */
  pct: number | undefined;
  /** Facilities that entered the quotient. */
  counted: number;
  /** Facilities left out of it, for any of the three reasons below. */
  excluded: number;
}

/**
 * The portfolio's share: of the facilities this lender is in, how much is
 * theirs (CA1 of LEN-2663).
 *
 * **Both sides of the quotient sum only the facilities that have the two
 * amounts captured.** A facility whose commitment nobody reported does not
 * drag its ceiling into the divisor: "we do not know" is not "they put in
 * nothing", and counting it as zero drives the figure down the more data is
 * missing -- which reads as a lender participating less, not as a gap.
 *
 * This is **not** the API's `participation_pct`, and the difference is not
 * rounding. That one divides what was committed by the ceiling of *every*
 * facility -- its own docstring says a trust without a commitment "counts in
 * the ceiling ... never in what was committed". Two facilities of $400, one
 * with $100 committed and the other with nothing captured, read 12.5% there
 * and 25% here.
 *
 * Ratified on 4-nov-2026: the CA1 rule governs, and both the Portfolio KPI row
 * and the Insights commitment card read it from here, so the same portfolio
 * can no longer show two percentages in two tabs (LEN-3334).
 *
 * **The amounts are expected already normalised to one currency**, which is
 * what `core_metrics.operations[]` carries:
 * `calculate_aggregated_total_credit_amount` (facilities-api,
 * `services/lender.py:501`) converts every operation whose currency differs
 * from the response's, writes the result back onto the operation, and converts
 * each commitment **in the same call as the ceiling it is a share of**
 * (LEN-2577). Summing them here is therefore summing one currency. Feeding
 * this function un-normalised amounts would not be (MR !1366 S2).
 *
 * `pct` is `undefined` when no facility has both amounts -- the caller says it
 * is unknown instead of painting a `0%`, which would claim no participation at
 * all.
 */
export function portfolioShare(
  facilities: readonly PortfolioFacility[] | null | undefined
): PortfolioShare {
  let committed = 0;
  let ceiling = 0;
  let counted = 0;
  let seen = 0;

  for (const facility of facilities ?? []) {
    seen += 1;
    const total = facility.total_credit_amount;
    // Case 05 (LEN-2365) leaves **both** sides: a commitment above its own
    // ceiling would push the portfolio's share past 100%, which is exactly
    // what that guard exists to prevent.
    if (
      !hasCommitment(facility) ||
      !isUsableCeiling(total) ||
      exceedsCeiling(facility, total)
    ) {
      continue;
    }

    counted += 1;
    committed += facility.committed_amount;
    ceiling += total;
  }

  return {
    pct: ceiling > 0 ? (committed / ceiling) * 100 : undefined,
    counted,
    excluded: seen - counted,
  };
}

/**
 * Whether comparing against the ceiling adds anything. Not for a sole lender:
 * the whole facility is theirs, and repeating the amount under another name
 * says nothing. Not when a figure is missing, the position is unknown, or the
 * commitment exceeds the ceiling.
 */
export function showsLenderShare(
  participation: LenderParticipation | null | undefined,
  totalCreditAmount: number | null | undefined
): boolean {
  if (!participation || isBilateral(participation)) return false;
  if (!isKnownPosition(participation)) return false;
  if (exceedsCeiling(participation, totalCreditAmount)) return false;

  return lenderSharePct(participation, totalCreditAmount) !== undefined;
}

/**
 * Why the card cannot compare, for its subtitle; `undefined` when it can.
 * The texts are the ones `docs/facility-card-states.md` pins.
 */
export function lenderShareGap(
  participation: LenderParticipation | null | undefined,
  totalCreditAmount: number | null | undefined
): string | undefined {
  if (!participation) return 'Your share not reported';
  if (!hasCommitment(participation)) return 'Commitment not reported';
  if (!isKnownPosition(participation)) return 'Position not reported';
  if (!isUsableCeiling(totalCreditAmount)) {
    return TOTAL_NOT_REPORTED;
  }

  return undefined;
}

/**
 * The card's title. It changes only when there is no ceiling: the amount shown
 * is then the commitment, and MAXIMUM CREDIT AMOUNT above it stops being true.
 */
export function lenderAmountTitle(
  participation: LenderParticipation | null | undefined,
  totalCreditAmount: number | null | undefined
): 'MAXIMUM CREDIT AMOUNT' | 'YOUR COMMITMENT' {
  return hasCommitment(participation) && !isUsableCeiling(totalCreditAmount)
    ? 'YOUR COMMITMENT'
    : 'MAXIMUM CREDIT AMOUNT';
}
